import React, { useMemo } from "react";
import { Card } from "../../../../shared/components/ui/Card";
import { InfoField } from "../../../../shared/components/ui/InfoField";
import type { New } from "../../../../api/client/types/new.types";
import {
  isMaintenanceReportType,
  isPoolRegulationReportType,
  isAdverseEventReportType,
} from "../../utils/reportTypeConstants";
import { MaintenanceNewDetailFields } from "../MaintenanceNewDetailFields/MaintenanceNewDetailFields";
import { PoolRegulationNewDetailFields } from "../PoolRegulationNewDetailFields/PoolRegulationNewDetailFields";
import { AdverseEventNewDetailFields } from "../AdverseEventNewDetailFields/AdverseEventNewDetailFields";
import { SwimmerInfoCard } from "../../../swimmers/components/SwimmerInfoCard/SwimmerInfoCard";

interface NewDetailContentProps {
  newData: New;
}

/**
 * NewDetailContent
 * Component for displaying news (novedad) detail information.
 * Shows common fields, swimmer information (if available), and report-type-specific fields.
 */
export const NewDetailContent: React.FC<NewDetailContentProps> = ({
  newData,
}) => {
  // Format event timestamp for display
  const eventTimestampDisplay = useMemo(() => {
    if (!newData.event_timestamp) return "—";
    const date = new Date(newData.event_timestamp);
    return new Intl.DateTimeFormat("es-CO", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  }, [newData.event_timestamp]);

  // Format created_at for display
  const createdAtDisplay = useMemo(() => {
    if (!newData.created_at) return "—";
    const date = new Date(newData.created_at);
    return new Intl.DateTimeFormat("es-CO", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  }, [newData.created_at]);

  // Check report type for conditional rendering
  const isMaintenance =
    newData.report_type &&
    isMaintenanceReportType(newData.report_type.id, [newData.report_type]);

  const isPoolRegulation =
    newData.report_type &&
    isPoolRegulationReportType(newData.report_type.id, [newData.report_type]);

  const isAdverseEvent =
    newData.report_type &&
    isAdverseEventReportType(newData.report_type.id, [newData.report_type]);

  return (
    <div className="flex flex-col gap-6 w-full max-w-4xl mx-auto">
      {/* Swimmer Information Section */}
      {newData.swimmer && <SwimmerInfoCard swimmer={newData.swimmer} />}

      {/* Event Information Section */}
      <Card variant="formSection">
        <h2 className="text-lg font-medium text-gray-800 mb-4">
          Información del Evento
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoField
            label="Fecha y Hora del Evento"
            value={eventTimestampDisplay}
          />
          <InfoField
            label="Tipo de Reporte"
            value={newData.report_type?.name}
          />
          <InfoField
            label="(W) Apellido del Oficial de Seguridad"
            value={newData.security_officer_name}
          />
          <InfoField label="Creado el" value={createdAtDisplay} />
        </div>

        {/* Event Description - Full width */}
        <div className="mt-4">
          <InfoField
            label="Descripción del Evento"
            value={newData.event_description}
          />
        </div>
      </Card>

      {/* Report Type Specific Information */}
      {isMaintenance && newData.maintenance_type && (
        <MaintenanceNewDetailFields
          maintenanceType={newData.maintenance_type}
        />
      )}

      {isPoolRegulation && (
        <PoolRegulationNewDetailFields
          poolRegulation={newData.pool_regulation}
          interventionLevel={newData.intervention_level}
        />
      )}

      {isAdverseEvent && (
        <AdverseEventNewDetailFields
          adverseEventType={newData.adverse_events_type}
          spentItems={newData.spent_items}
          eventSpecificFactor={newData.event_specific_factor}
          adverseEventInterventionLevel={newData.adverse_event_intervention_level}
        />
      )}

      {/* Other report types can be added here in the future */}
    </div>
  );
};
